import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CircularProgress, Typography } from "@mui/material";
import moment from "moment";
import {
  JobPricingDetailsState,
  JobPricingHeaderDetails,
  JobPricingPercentileData,
  JobPricingPercentileDataResponse,
  JobPricingPercentileDetails,
  JobPricingReportDetails,
  JobPricingReportDetailsResponse,
  JobPricingReportViewBy,
  SuccessProfileDetailsQueryParams,
  SuccessProfileDetailsResponse
} from "../../helpers/models/JobPricingDetailModels";
import {
  getPercentileData,
  getPricingFilters,
  getReportDetails,
  getSuccessProfileDetails
} from "../../services/JobPricingReportService";
import { JobPricingReportConstants } from "../../constants/JobPricingReportConstants";
import { JobPricingPayloads } from "../../helpers/models/JobPricingSearchModels";
import { useTranslation } from "react-i18next";

export const GradientCircularProgress = () => {
  const { t } = useTranslation();
  return (
    <div className="chart-loading-container">
      <svg width={0} height={0}>
        <defs>
          <linearGradient
            id="chart_circular_gradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#008369" />
            <stop offset="100%" stopColor="#A7F0BA" />
          </linearGradient>
        </defs>
      </svg>
      <CircularProgress
        sx={{ "svg circle": { stroke: "url(#chart_circular_gradient)" } }}
      />
      <Typography variant="body2" sx={{ marginTop: 2 }}>
        {t("pa.loadingPleaseWait")}
      </Typography>
    </div>
  );
};

export const getSortedPercentileDataList = (
  percentileData: JobPricingPercentileData[]
) => {
  return percentileData.sort((a, b) => {
    if (a.percentile < b.percentile) return -1;
    else if (a.percentile > b.percentile) return 1;
    else return 0;
  });
};

const getDefaultPercentileDataList = (
  percentileData: JobPricingPercentileData[]
) => {
  const defaultPercentileData = [
    ...JobPricingReportConstants.DEFAULT_PERCENTILE_VALUES,
    JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_VALUES
  ];
  return percentileData.filter(
    (data: JobPricingPercentileData, index: number) =>
      defaultPercentileData.includes(data.percentile)
  );
};

const getDefaultTargetData = () => {
  return JobPricingReportConstants.DEFAULT_PERCENTILE_DATA;
};

const getEffectiveDate = (effectiveDate: string) => {
  if (!effectiveDate) return "-";
  return moment.utc(effectiveDate).format("DD MMM, YYYY");
};

export const getReportDetailsList = (
  reportDetails: JobPricingReportDetails[],
  selectedTargetData: JobPricingPercentileData
) => {
  let data: JobPricingReportDetails[] = [];
  let valuesCount = 0;
  data = reportDetails
    .filter(
      (data: JobPricingReportDetails, index: number) =>
        data.factName &&
        data.compensationFactKey.toString() !==
          JobPricingReportConstants.PERVIOUS_YEAR_COMPENSATION_KEY
    )
    .map((data: JobPricingReportDetails, index: number) => {
      const marketPositionsList = (data.marketPositions || "").split("|");
      const marketValuesList = (data.marketValues || "").split("|");
      const clientValuesList = (data.clientValues || "").split("|");
      let percentileDetailsList: JobPricingPercentileDetails[] = [];
      const targetData =
        selectedTargetData || JobPricingReportConstants.DEFAULT_PERCENTILE_DATA;
      let marketValuesCount = 0;
      let clientValuesCount = 0;
      marketPositionsList.forEach((value: string, index: number) => {
        const marketValue = Math.round(
          +((marketValuesList[index] || "").trim() || "")
        );
        valuesCount += marketValue;
        marketValuesCount += marketValue;
        const clientValue = Math.round(
          +((clientValuesList[index] || "").trim() || "")
        );
        valuesCount += clientValue;
        clientValuesCount += clientValue;
        if (targetData.percentile === value) {
          data.targetMarket = marketValue;
          data.targetOrg = clientValue;
        }
        if (
          JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_VALUES === value
        ) {
          data.avgMarket = marketValue;
          data.avgOrg = clientValue;
        }
        percentileDetailsList.push({
          percentileValue: value,
          marketValue: marketValue,
          clientValue: clientValue
        } as JobPricingPercentileDetails);
      });
      data.marketCount = marketValuesCount;
      data.clientCount = clientValuesCount;
      data.percentileDetails = percentileDetailsList.sort((a, b) => {
        if (a.percentileValue < b.percentileValue) return -1;
        else if (a.percentileValue > b.percentileValue) return 1;
        else return 0;
      });
      data.targetMarket = data.targetMarket || 0;
      data.targetOrg = data.targetOrg || 0;
      data.avgMarket = data.avgMarket || 0;
      data.avgOrg = data.avgOrg || 0;
      const avgMarketPercentage = Number(data.targetMarket)
        ? (Number(data.targetMarket) / Number(data.avgMarket)) * 100
        : 0;
      data.avgMarketPercentage = Math.round(avgMarketPercentage);
      const avgOrgPercentage = Number(data.targetMarket)
        ? (Number(data.avgOrg) / Number(data.targetMarket)) * 100
        : 0;
      data.avgOrgPercentage = Math.round(avgOrgPercentage);
      const orgAvgSalaryValue = Number(data.avgOrg) - Number(data.targetMarket);
      const orgAvgSalaryPercentage =
        orgAvgSalaryValue && Number(data.targetMarket)
          ? (orgAvgSalaryValue / Number(data.targetMarket)) * 100
          : orgAvgSalaryValue
          ? 100
          : 0;
      data.orgAvgSalaryStatus = orgAvgSalaryPercentage > 0 ? "more" : "less";
      data.orgAvgSalaryPercentage = Math.round(
        orgAvgSalaryPercentage > 0
          ? orgAvgSalaryPercentage
          : orgAvgSalaryPercentage * -1
      );
      data.orgAvgSalaryValue =
        orgAvgSalaryValue > 0
          ? orgAvgSalaryValue.toLocaleString()
          : (orgAvgSalaryValue * -1).toLocaleString();
      return data;
    });
  return valuesCount && valuesCount > 0 ? data : [];
};

const reportInitialState: JobPricingDetailsState = {
  reportViewBy: JobPricingReportViewBy.JOB,
  headerDetails: {} as JobPricingHeaderDetails,
  percentileDataList: [],
  targetData: getDefaultTargetData(),
  reportDetails: [],
  previousYearReportDetails: [],
  isViewSuccessProfile: false,
  isToggleTableView: false,
  publicationMarketDate: "",
  submissionOrgDate: "",
  showTableCompElements: [],
  showSelectedPercentileList: [],
  isPercentileDataLoading: false,
  isReportDetailsLoading: true,
  isReturnToDataList: true,
  clientName: "",
  isSuccessProfileDetailsLoading: false,
  successProfileDetails: null,
  isReportDetailsApiFailed: false
};

export const getPercentileDataThunk = createAsyncThunk(
  "jobPricing/percentileData",
  async (payload: any, thunkApi) => {
    try {
      return await getPercentileData();
    } catch (error) {
      return thunkApi.rejectWithValue({ error });
    }
  }
);

export const getReportDetailsThunk = createAsyncThunk(
  "jobPricing/reportDetails",
  async (payload: JobPricingPayloads, thunkApi) => {
    try {
      return await getReportDetails(payload);
    } catch (error) {
      return thunkApi.rejectWithValue({ error });
    }
  }
);

export const getSuccessProfileDataThunk = createAsyncThunk(
  "jobPricing/successProfileDetails",
  async (payload: SuccessProfileDetailsQueryParams, thunkApi) => {
    try {
      return await getSuccessProfileDetails(payload);
    } catch (error) {
      return thunkApi.rejectWithValue({ error });
    }
  }
);

export const getPricingFiltersThunk = createAsyncThunk(
  "jobPricing/pricingFilters",
  async (payload?: any, thunkApi?: any) => {
    try {
      return await getPricingFilters(payload);
    } catch (error) {
      return thunkApi.rejectWithValue({ error });
    }
  }
);

export const reportDetailsSlice = createSlice({
  name: "SJPR_Details_Slice",
  initialState: reportInitialState,
  reducers: {
    resetReportDetailsState: () => reportInitialState,
    resetPricingDetailsState: (state) => {
      state.reportViewBy = JobPricingReportViewBy.JOB;
      state.headerDetails = {} as JobPricingHeaderDetails;
      state.targetData = getDefaultTargetData();
      state.reportDetails = [];
      state.previousYearReportDetails = [];
      state.isViewSuccessProfile = false;
      state.isToggleTableView = false;
      state.publicationMarketDate = "";
      state.submissionOrgDate = "";
      state.showTableCompElements = [];
      state.showSelectedPercentileList = [];
      state.isPercentileDataLoading = false;
      state.isReportDetailsLoading = true;
      state.isReturnToDataList = true;
      state.clientName = "";
      state.isSuccessProfileDetailsLoading = false;
      state.successProfileDetails = null;
      state.isReportDetailsApiFailed = false;
    },
    resetPricingDetailsTitle: (state) => {
      state.headerDetails = {} as JobPricingHeaderDetails;
    },
    updateReportViewBy: (state, action) => {
      state.reportViewBy = action.payload;
    },
    updateReportHeaderDetails: (state, action) => {
      state.headerDetails = action.payload;
    },
    updatePercentileDataList: (state, action) => {
      state.percentileDataList = action.payload;
    },
    setDefaultTargetData: (state, action) => {
      state.targetData = action.payload;
    },
    updateReportDetails: (state, action) => {
      state.reportDetails = action.payload;
    },
    setViewSuccessProfile: (state, action) => {
      state.isViewSuccessProfile = action.payload;
    },
    setToggleTableView: (state, action) => {
      state.isToggleTableView = action.payload;
    },
    setTableCompElements: (state, action) => {
      state.showTableCompElements = action.payload;
    },
    setShowSelectedPercentileList: (state, action) => {
      state.showSelectedPercentileList = action.payload;
    },
    updateReportDetailsApiFailed: (state, action) => {
      state.isReportDetailsApiFailed = action.payload;
    },
    setReturnToDataList: (state, action) => {
      state.isReturnToDataList = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getPercentileDataThunk.pending, (state, action) => {
      state.isPercentileDataLoading = true;
    });
    builder.addCase(getPercentileDataThunk.fulfilled, (state, action) => {
      const response: JobPricingPercentileDataResponse = action.payload.data;
      state.percentileDataList =
        getSortedPercentileDataList(response.data) || [];
      state.showSelectedPercentileList = getDefaultPercentileDataList(
        state.percentileDataList
      );
      state.isPercentileDataLoading = false;
    });
    builder.addCase(getPercentileDataThunk.rejected, (state, action) => {
      state.percentileDataList = [];
      state.isPercentileDataLoading = false;
    });
    builder.addCase(getReportDetailsThunk.pending, (state, action) => {
      state.isReportDetailsLoading = true;
      state.isReportDetailsApiFailed = false;
      if (
        !state.showSelectedPercentileList ||
        state.showSelectedPercentileList.length === 0
      ) {
        state.showSelectedPercentileList = getDefaultPercentileDataList(
          state.percentileDataList
        );
      }
    });
    builder.addCase(getReportDetailsThunk.fulfilled, (state, action) => {
      const response: JobPricingReportDetailsResponse = action.payload.data || {};
      const pricingDetailsList = response?.data?.pricingDetail || [];
      state.clientName = response?.data?.clientName || "";
      state.previousYearReportDetails =
        pricingDetailsList.filter(
          (data: JobPricingReportDetails, index: number) =>
            data.factName &&
            data.compensationFactKey.toString() ===
              JobPricingReportConstants.PERVIOUS_YEAR_COMPENSATION_KEY
        ) || [];
      state.reportDetails = getReportDetailsList(
        pricingDetailsList,
        state.targetData
      );
      state.showTableCompElements = state.reportDetails.filter(
        (data: JobPricingReportDetails) => data.marketCount && data.marketCount > 0
      ).map(
        (data: JobPricingReportDetails) => data.factName
      );
      if (
        !state.showSelectedPercentileList ||
        state.showSelectedPercentileList.length === 0
      ) {
        state.showSelectedPercentileList = getDefaultPercentileDataList(
          state.percentileDataList
        );
      }
      const reportData = state.reportDetails[0] || {};
      state.publicationMarketDate = getEffectiveDate(
        reportData.marketEffectiveDate
      );
      state.submissionOrgDate = getEffectiveDate(reportData.orgEffectiveDate);
      state.isReportDetailsLoading = false;
    });
    builder.addCase(getReportDetailsThunk.rejected, (state, action) => {
      state.isReportDetailsLoading = false;
      state.reportDetails = [];
      state.publicationMarketDate = getEffectiveDate("");
      state.submissionOrgDate = getEffectiveDate("");
      if (
        !state.showSelectedPercentileList ||
        state.showSelectedPercentileList.length === 0
      ) {
        state.showSelectedPercentileList = getDefaultPercentileDataList(
          state.percentileDataList
        );
      }
      state.isReportDetailsApiFailed = true;
    });
    builder.addCase(getSuccessProfileDataThunk.pending, (state, action) => {
      state.isSuccessProfileDetailsLoading = true;
    });
    builder.addCase(getSuccessProfileDataThunk.fulfilled, (state, action) => {
      const response: SuccessProfileDetailsResponse = action.payload.data;
      const successProfileDetails = response.data;
      successProfileDetails.skills = (successProfileDetails.skills || []).sort(
        (a, b) => b.level - a.level
      );
      successProfileDetails.behavioralCompetencies = (
        successProfileDetails.behavioralCompetencies || []
      ).sort((a, b) => b.level - a.level);
      state.successProfileDetails = successProfileDetails;
      state.isSuccessProfileDetailsLoading = false;
    });
    builder.addCase(getSuccessProfileDataThunk.rejected, (state, action) => {
      state.successProfileDetails = null;
      state.isSuccessProfileDetailsLoading = false;
    });
  }
});

export const {
  resetReportDetailsState,
  resetPricingDetailsState,
  updateReportViewBy,
  updateReportHeaderDetails,
  updatePercentileDataList,
  setDefaultTargetData,
  updateReportDetails,
  setViewSuccessProfile,
  setToggleTableView,
  setTableCompElements,
  setShowSelectedPercentileList,
  updateReportDetailsApiFailed,
  setReturnToDataList,
  resetPricingDetailsTitle
} = reportDetailsSlice.actions;

export default reportDetailsSlice.reducer;
